import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { companiesApi, reportsApi, exportApi } from '@/lib/api';
import { toast } from 'sonner';
import { Download, BarChart3, Clock, Home, Users, TrendingUp, UserX } from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';
import { motion } from 'framer-motion';

const COLORS = {
  homeowners: '#22c55e',
  renters: '#3b82f6',
  pending: '#eab308',
};

export default function ReportsPage() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [tenureData, setTenureData] = useState(null);
  const [statesData, setStatesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    loadReports();
  }, [selectedCompany]);

  const loadCompanies = async () => {
    try {
      const response = await companiesApi.getAll();
      setCompanies(response.data);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      const companyId = selectedCompany || undefined;
      const [tenureRes, statesRes] = await Promise.all([
        reportsApi.getTenure(companyId),
        reportsApi.getStatesDistribution(companyId),
      ]);
      setTenureData(tenureRes.data);
      setStatesData(statesRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleExportTenure = async () => {
    setExporting(true);
    try {
      const response = await exportApi.tenureReport(selectedCompany || undefined);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tenure_report_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const pieData = tenureData
    ? [
        { name: 'Homeowners', value: tenureData.homeowners.count, color: COLORS.homeowners },
        { name: 'Renters', value: tenureData.renters.count, color: COLORS.renters },
        { name: 'Pending', value: tenureData.pending.count, color: COLORS.pending },
      ].filter((d) => d.value > 0)
    : [];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground mt-1">
            Tenure and homeowner statistics
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Select
            value={selectedCompany}
            onValueChange={(value) => setSelectedCompany(value === 'all' ? '' : value)}
          >
            <SelectTrigger className="w-[200px] bg-background border-input" data-testid="report-company-filter">
              <SelectValue placeholder="All Companies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {companies.map((company) => (
                <SelectItem key={company.id} value={company.id}>
                  {company.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={handleExportTenure}
            disabled={exporting}
            className="bg-[#E31837] hover:bg-[#c41530] text-white"
            data-testid="export-tenure-report"
          >
            {exporting ? (
              <div className="spinner w-4 h-4" />
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export Report
              </>
            )}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-6"
        >
          {/* Overall Tenure Stats */}
          {tenureData?.overall && (
            <motion.div variants={itemVariants}>
              <Card className="bg-gradient-to-r from-[#E31837]/10 to-[#E31837]/5 border-[#E31837]/20">
                <CardContent className="py-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-lg bg-[#E31837]/20">
                        <TrendingUp className="w-6 h-6 text-[#E31837]" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Overall Company Tenure</p>
                        <p className="text-2xl font-bold">{tenureData.total_employees} employees</p>
                      </div>
                    </div>
                    <div className="flex gap-8">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-[#E31837]">{tenureData.overall.avg_tenure_years}</p>
                        <p className="text-sm text-muted-foreground">Average (years)</p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-[#E31837]">{tenureData.overall.median_tenure_years}</p>
                        <p className="text-sm text-muted-foreground">Median (years)</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Tenure Stats Cards */}
          <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div variants={itemVariants}>
              <Card className="bg-card border-border" data-testid="homeowners-stats">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Home className="w-4 h-4 text-green-500" />
                    Homeowners
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-green-500">
                    {tenureData?.homeowners.count || 0}
                  </p>
                  <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                    <p>Avg: <span className="text-foreground font-medium">{tenureData?.homeowners.avg_tenure_years || 0} years</span></p>
                    <p>Median: <span className="text-foreground font-medium">{tenureData?.homeowners.median_tenure_years || 0} years</span></p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Card className="bg-card border-border" data-testid="renters-stats">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-500" />
                    Renters
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-blue-500">
                    {tenureData?.renters.count || 0}
                  </p>
                  <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                    <p>Avg: <span className="text-foreground font-medium">{tenureData?.renters.avg_tenure_years || 0} years</span></p>
                    <p>Median: <span className="text-foreground font-medium">{tenureData?.renters.median_tenure_years || 0} years</span></p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Card className="bg-card border-border" data-testid="pending-stats">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="w-4 h-4 text-yellow-500" />
                    Pending Verification
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-yellow-500">
                    {tenureData?.pending.count || 0}
                  </p>
                  <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                    <p>Avg: <span className="text-foreground font-medium">{tenureData?.pending.avg_tenure_years || 0} years</span></p>
                    <p>Median: <span className="text-foreground font-medium">{tenureData?.pending.median_tenure_years || 0} years</span></p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>

          {/* Left Employees Section */}
          {tenureData?.left && tenureData.left.count > 0 && (
            <motion.div variants={itemVariants}>
              <Card className="bg-gradient-to-r from-red-500/10 to-red-500/5 border-red-500/20">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <UserX className="w-5 h-5 text-red-500" />
                    Employees Who Left
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {/* Total Left */}
                    <div className="text-center p-4 rounded-lg bg-card/50">
                      <p className="text-4xl font-bold text-red-500">{tenureData.left.count}</p>
                      <p className="text-sm text-muted-foreground mt-1">Total Left</p>
                    </div>
                    
                    {/* Tenure Stats */}
                    <div className="text-center p-4 rounded-lg bg-card/50">
                      <p className="text-2xl font-bold">{tenureData.left.avg_tenure_years}</p>
                      <p className="text-sm text-muted-foreground">Avg Tenure (years)</p>
                      <p className="text-lg font-medium mt-1">{tenureData.left.median_tenure_years}</p>
                      <p className="text-xs text-muted-foreground">Median</p>
                    </div>
                    
                    {/* Homeowner Breakdown */}
                    <div className="text-center p-4 rounded-lg bg-card/50">
                      <div className="flex items-center justify-center gap-2">
                        <Home className="w-4 h-4 text-green-500" />
                        <span className="text-2xl font-bold text-green-500">{tenureData.left.homeowners_pct}%</span>
                      </div>
                      <p className="text-sm text-muted-foreground">Homeowners</p>
                      <p className="text-xs text-muted-foreground">({tenureData.left.homeowners_count} people)</p>
                      <p className="text-sm font-medium mt-2">
                        Median: <span className="text-green-400">{tenureData.left.homeowners_median_tenure_years} yrs</span>
                      </p>
                    </div>
                    
                    {/* Renter Breakdown */}
                    <div className="text-center p-4 rounded-lg bg-card/50">
                      <div className="flex items-center justify-center gap-2">
                        <Users className="w-4 h-4 text-blue-500" />
                        <span className="text-2xl font-bold text-blue-500">{tenureData.left.renters_pct}%</span>
                      </div>
                      <p className="text-sm text-muted-foreground">Renters</p>
                      <p className="text-xs text-muted-foreground">({tenureData.left.renters_count} people)</p>
                      <p className="text-sm font-medium mt-2">
                        Median: <span className="text-blue-400">{tenureData.left.renters_median_tenure_years} yrs</span>
                      </p>
                    </div>
                  </div>
                  
                  {/* Visual bar */}
                  <div className="mt-4 h-4 rounded-full overflow-hidden bg-card flex">
                    <div 
                      className="bg-green-500 h-full" 
                      style={{ width: `${tenureData.left.homeowners_pct}%` }}
                      title={`Homeowners: ${tenureData.left.homeowners_pct}%`}
                    />
                    <div 
                      className="bg-blue-500 h-full" 
                      style={{ width: `${tenureData.left.renters_pct}%` }}
                      title={`Renters: ${tenureData.left.renters_pct}%`}
                    />
                    <div 
                      className="bg-yellow-500 h-full" 
                      style={{ width: `${tenureData.left.pending_pct}%` }}
                      title={`Pending: ${tenureData.left.pending_pct}%`}
                    />
                  </div>
                  <div className="flex justify-center gap-6 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500"></span> Homeowners</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500"></span> Renters</span>
                    {tenureData.left.pending_pct > 0 && (
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-500"></span> Pending</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <motion.div variants={itemVariants}>
              <Card className="bg-card border-border" data-testid="homeowner-pie-chart">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    Homeowner Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {pieData.length > 0 ? (
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={5}
                            dataKey="value"
                            label={({ name, percent }) =>
                              `${name} ${(percent * 100).toFixed(0)}%`
                            }
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              background: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      No employee data available
                    </div>
                  )}
                  <div className="flex justify-center gap-6 mt-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ background: COLORS.homeowners }} />
                      <span className="text-sm text-muted-foreground">Homeowners</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ background: COLORS.renters }} />
                      <span className="text-sm text-muted-foreground">Renters</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ background: COLORS.pending }} />
                      <span className="text-sm text-muted-foreground">Pending</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Card className="bg-card border-border" data-testid="tenure-comparison-chart">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" />
                    Median Tenure Comparison (Years)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {tenureData ? (
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={[
                            {
                              name: 'Homeowners',
                              medianTenure: tenureData.homeowners.median_tenure_years,
                              count: tenureData.homeowners.count,
                              fill: COLORS.homeowners,
                            },
                            {
                              name: 'Renters',
                              medianTenure: tenureData.renters.median_tenure_years,
                              count: tenureData.renters.count,
                              fill: COLORS.renters,
                            },
                            {
                              name: 'Pending',
                              medianTenure: tenureData.pending.median_tenure_years,
                              count: tenureData.pending.count,
                              fill: COLORS.pending,
                            },
                          ]}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                          <YAxis stroke="hsl(var(--muted-foreground))" />
                          <Tooltip
                            contentStyle={{
                              background: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                            }}
                            formatter={(value) => [`${value} years`, 'Median Tenure']}
                          />
                          <Legend />
                          <Bar dataKey="medianTenure" name="Median Tenure" radius={[4, 4, 0, 0]}>
                            {[
                              { fill: COLORS.homeowners },
                              { fill: COLORS.renters },
                              { fill: COLORS.pending },
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      No tenure data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* State Distribution */}
          {statesData.length > 0 && (
            <motion.div variants={itemVariants}>
              <Card className="bg-card border-border" data-testid="states-distribution-chart">
                <CardHeader>
                  <CardTitle className="text-lg">Employee Distribution by State</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={statesData.slice(0, 15)}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <XAxis dataKey="state" stroke="hsl(var(--muted-foreground))" />
                        <YAxis stroke="hsl(var(--muted-foreground))" />
                        <Tooltip
                          contentStyle={{
                            background: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                          }}
                        />
                        <Legend />
                        <Bar dataKey="homeowners" name="Homeowners" fill={COLORS.homeowners} stackId="a" />
                        <Bar dataKey="renters" name="Renters" fill={COLORS.renters} stackId="a" />
                        <Bar dataKey="pending" name="Pending" fill={COLORS.pending} stackId="a" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </motion.div>
      )}
    </div>
  );
}
